import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useGallery,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import { AmenityIcon, amenityKeywordSets } from './_amenityIcons';

// ============================================================================
// Smart Photo Selection — pick an amenity-flavored gallery photo as the base
// ============================================================================

const AMENITY_KEYWORDS = [
  'pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'amenities',
  'recreation', 'courtyard', 'grill', 'spa', 'theater', 'rooftop',
];

function photoText(photo: PhotoGallery): string {
  return [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

// Whole-word (with a simple plural) match so a keyword like "spa" can't hit
// "space" or "car" hit "carpet".
function countWordHits(text: string, keywords: string[]): number {
  if (!text) return 0;
  let hits = 0;
  for (const kw of keywords) {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`\\b${escaped}(?:s|es)?\\b`, 'i').test(text)) hits += 1;
  }
  return hits;
}

function useAmenityPhotoIndex(): number {
  const gallery = useGallery();
  return useMemo(() => {
    if (!gallery.length) return 3;
    let bestIndex = -1;
    let bestScore = 0;
    for (let i = 0; i < gallery.length; i++) {
      const score = countWordHits(photoText(gallery[i]), AMENITY_KEYWORDS);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }
    if (bestIndex >= 0) return bestIndex;
    return Math.min(3, gallery.length - 1);
  }, [gallery]);
}

function makeImageSlotDef(index: number): SlotGroupDefinition {
  return {
    label: 'Amenities Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Best-matching (and de-duplicated) gallery photo per amenity
// ============================================================================
//
// Each amenity tab should show the gallery photo that best matches its title,
// and two different amenities should not land on the same photo when the
// gallery has enough variety. We score every (amenity, photo) pair and greedily
// assign the strongest matches first, never reusing a photo. Matching is
// deliberately conservative: curated category synonyms count more than the
// amenity's own (often generic) title words, and a pair must clear
// MIN_MATCH_SCORE before it is eligible at all.

const CATEGORY_WEIGHT = 2;
const TITLE_WEIGHT = 1;
const MIN_MATCH_SCORE = 2;

function assignAmenityImages(
  titles: string[],
  gallery: PhotoGallery[],
): (string | null)[] {
  const result: (string | null)[] = titles.map(() => null);
  if (!gallery.length) return result;

  const galleryText = gallery.map(photoText);

  const pairs: { ti: number; pi: number; score: number }[] = [];
  titles.forEach((title, ti) => {
    const { category, title: titleWords } = amenityKeywordSets(title);
    if (!category.length && !titleWords.length) return;
    galleryText.forEach((text, pi) => {
      const score =
        countWordHits(text, category) * CATEGORY_WEIGHT +
        countWordHits(text, titleWords) * TITLE_WEIGHT;
      if (score >= MIN_MATCH_SCORE) pairs.push({ ti, pi, score });
    });
  });

  pairs.sort((a, b) => b.score - a.score);
  const usedPhotos = new Set<number>();
  for (const { ti, pi } of pairs) {
    if (result[ti] || usedPhotos.has(pi)) continue;
    result[ti] = gallery[pi].image || null;
    usedPhotos.add(pi);
  }
  return result;
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Amenities Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const ctaDef: SlotGroupDefinition = {
  label: 'View All Amenities CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// Six standout-amenity tiles. Rather than iterating the amenities collection,
// each tile is its own slot group so an editor can hand-pick exactly which six
// amenities headline the section — and a generator preselects the property's
// top amenities on demand. The tile icon is derived from the chosen title via
// the ported amenity icon catalog.
//
// Written as fully-inlined object literals (no shared factory, no referenced
// consts) so the preview system's static schema extractor can resolve them.
const amenity1Def: SlotGroupDefinition = {
  label: 'Standout Amenity 1',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[0].title', defaultValue: '24/7 Fitness Center' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 0, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Amenities for you section.",
    context: 'Tile 1 of 6 in the Amenities for you grid — pick the single best headline amenity; each of the six tiles must be a different amenity.',
    sourceCollection: 'amenities',
  },
};

const amenity2Def: SlotGroupDefinition = {
  label: 'Standout Amenity 2',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[1].title', defaultValue: 'Study Lounge' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 1, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Amenities for you section.",
    context: 'Tile 2 of 6 in the Amenities for you grid — pick a different amenity from the other five tiles.',
    sourceCollection: 'amenities',
  },
};

const amenity3Def: SlotGroupDefinition = {
  label: 'Standout Amenity 3',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[2].title', defaultValue: 'Resort-Style Pool' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 2, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Amenities for you section.",
    context: 'Tile 3 of 6 in the Amenities for you grid — pick a different amenity from the other five tiles.',
    sourceCollection: 'amenities',
  },
};

const amenity4Def: SlotGroupDefinition = {
  label: 'Standout Amenity 4',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[3].title', defaultValue: 'Yoga Room' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 3, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Amenities for you section.",
    context: 'Tile 4 of 6 in the Amenities for you grid — pick a different amenity from the other five tiles.',
    sourceCollection: 'amenities',
  },
};

const amenity5Def: SlotGroupDefinition = {
  label: 'Standout Amenity 5',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[4].title', defaultValue: 'Dog Park' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 4, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Amenities for you section.",
    context: 'Tile 5 of 6 in the Amenities for you grid — pick a different amenity from the other five tiles.',
    sourceCollection: 'amenities',
  },
};

const amenity6Def: SlotGroupDefinition = {
  label: 'Standout Amenity 6',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[5].title', defaultValue: 'Furnished Rooms' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 5, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Amenities for you section.",
    context: 'Tile 6 of 6 in the Amenities for you grid — pick a different amenity from the other five tiles.',
    sourceCollection: 'amenities',
  },
};

// Representative titles for company-theme previews (no property to generate
// against). Each resolves to a distinct icon through the amenity catalog.
const TILE_PREVIEW_TITLES = [
  '24/7 Fitness Center',
  'Study Lounge',
  'Resort-Style Pool',
  'Yoga Room',
  'Dog Park',
  'Furnished Rooms',
];

// ============================================================================
// Presentational amenity tile (no hooks — lives inside each EditableSlotGroup)
// ============================================================================

interface AmenityTileProps {
  title: string;
  index: number;
  active: boolean;
  onSelect: (index: number) => void;
}

function AmenityTile({ title, index, active, onSelect }: AmenityTileProps) {
  return (
    <button
      type="button"
      className={`afy-tile${active ? ' afy-tile--active' : ''}`}
      aria-pressed={active}
      // Only swap the photo when a tile is actually chosen (click / keyboard
      // activation). Selecting on hover/focus caused the photo to flash between
      // amenities as the pointer moved across the tiles.
      onClick={() => onSelect(index)}
    >
      <span className="afy-tile-icon" aria-hidden="true">
        <AmenityIcon title={title} />
      </span>
      <span className="afy-tile-label">{title}</span>
    </button>
  );
}

// ============================================================================
// Component
// ============================================================================

export function AmenitiesForYou() {
  const gallery = useGallery();
  const photoIndex = useAmenityPhotoIndex();
  const imgDef = useMemo(() => makeImageSlotDef(photoIndex), [photoIndex]);

  const img = useSlotGroup('amenitiesforyou.image', imgDef);
  const cta = useSlotGroup('amenitiesforyou.cta_link', ctaDef);

  const amenity1 = useSlotGroup('amenitiesforyou.amenity_1', amenity1Def);
  const amenity2 = useSlotGroup('amenitiesforyou.amenity_2', amenity2Def);
  const amenity3 = useSlotGroup('amenitiesforyou.amenity_3', amenity3Def);
  const amenity4 = useSlotGroup('amenitiesforyou.amenity_4', amenity4Def);
  const amenity5 = useSlotGroup('amenitiesforyou.amenity_5', amenity5Def);
  const amenity6 = useSlotGroup('amenitiesforyou.amenity_6', amenity6Def);

  // Resolve each tile's label — chosen/generated value first, preview title as
  // the non-property fallback so the widget never renders an empty tile.
  const amenityTitles = [
    amenity1.title?.trim() || TILE_PREVIEW_TITLES[0],
    amenity2.title?.trim() || TILE_PREVIEW_TITLES[1],
    amenity3.title?.trim() || TILE_PREVIEW_TITLES[2],
    amenity4.title?.trim() || TILE_PREVIEW_TITLES[3],
    amenity5.title?.trim() || TILE_PREVIEW_TITLES[4],
    amenity6.title?.trim() || TILE_PREVIEW_TITLES[5],
  ];

  // Interactive tile state: the selected amenity highlights its tile and swaps
  // the photo to match.
  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = activeIndex < amenityTitles.length ? activeIndex : 0;
  const activeTitle = amenityTitles[safeIndex];

  // Resolve the image shown for the active tile: give every amenity its own
  // best-matching (and distinct) gallery photo, then show the active tile's.
  // When nothing matches, stay null so the base editable image shows instead of
  // an unrelated photo.
  const amenityImages = useMemo(
    () => assignAmenityImages(amenityTitles, gallery),
    // amenityTitles is rebuilt each render; key the memo on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amenityTitles.join(' '), gallery],
  );

  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up amenities-for-you w-full overflow-hidden bg-background py-12 md:py-16 lg:py-20"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 sm:px-6 lg:grid lg:grid-cols-[minmax(0,7fr)_minmax(0,6fr)] lg:items-start lg:gap-16 lg:px-8">
        {/* Left: photo with decorative offset primary-color rectangle */}
        <div className="afy-media">
          <div className="afy-media-accent" aria-hidden="true" />
          <div className="afy-media-frame">
            <EditableSlotGroup
              groupPath="amenitiesforyou.image"
              definition={imgDef}
              className="afy-media-slot"
            >
              {img.image ? (
                <VibesitesImage
                  url={img.image}
                  alt={img.alt || ''}
                  sizes="(min-width: 1024px) 40rem, 100vw"
                  className="afy-media-img"
                />
              ) : (
                <div className="afy-media-img afy-media-img--empty" />
              )}
            </EditableSlotGroup>

            {/* Selected amenity photo — swaps in over the base image when the
                active tile matches a relevant gallery photo. Every distinct
                match is rendered once and kept mounted so it is decoded ahead
                of time; selecting a tile just crossfades opacity. */}
            {amenityImages.map((url, i) =>
              url ? (
                <VibesitesImage
                  key={url}
                  url={url}
                  alt={safeIndex === i ? activeTitle || '' : ''}
                  aria-hidden={safeIndex !== i}
                  sizes="(min-width: 1024px) 40rem, 100vw"
                  className={`afy-media-overlay${safeIndex === i ? ' afy-media-overlay--active' : ''}`}
                />
              ) : null,
            )}
          </div>
        </div>

        {/* Right: heading, body, CTA, and amenity tile grid */}
        <div className="afy-content flex w-full flex-col">
          <RichTextContent
            path="amenitiesforyou.heading"
            label="Heading"
            defaultValue="<p>Amenities for you</p>"
            as="h2"
            className="afy-heading"
          />

          <RichTextContent
            path="amenitiesforyou.body"
            label="Body Text"
            as="div"
            className="afy-body"
          />

          <EditableSlotGroup
            groupPath="amenitiesforyou.cta_link"
            definition={ctaDef}
            className="afy-cta-wrap"
          >
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className="site-cta site-cta--secondary afy-cta"
            >
              <RichTextContent
                path="amenitiesforyou.cta_label"
                label="CTA Label"
                defaultValue="<p>View All Amenities</p>"
                as="span"
              />
            </a>
          </EditableSlotGroup>

          <div className="afy-tiles">
            <EditableSlotGroup groupPath="amenitiesforyou.amenity_1" definition={amenity1Def} className="afy-tile-slot">
              <AmenityTile title={amenityTitles[0]} index={0} active={safeIndex === 0} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="amenitiesforyou.amenity_2" definition={amenity2Def} className="afy-tile-slot">
              <AmenityTile title={amenityTitles[1]} index={1} active={safeIndex === 1} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="amenitiesforyou.amenity_3" definition={amenity3Def} className="afy-tile-slot">
              <AmenityTile title={amenityTitles[2]} index={2} active={safeIndex === 2} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="amenitiesforyou.amenity_4" definition={amenity4Def} className="afy-tile-slot">
              <AmenityTile title={amenityTitles[3]} index={3} active={safeIndex === 3} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="amenitiesforyou.amenity_5" definition={amenity5Def} className="afy-tile-slot">
              <AmenityTile title={amenityTitles[4]} index={4} active={safeIndex === 4} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="amenitiesforyou.amenity_6" definition={amenity6Def} className="afy-tile-slot">
              <AmenityTile title={amenityTitles[5]} index={5} active={safeIndex === 5} onSelect={setActiveIndex} />
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* ── Layout containers ── */
        .afy-content {
          min-width: 0;
        }

        /* ── Left column: photo + offset primary-color rectangle ── */
        .afy-media {
          position: relative;
          width: 100%;
          isolation: isolate;
        }
        /* Solid primary-color rectangle offset to the bottom-left, poking out
           from behind the photo. Deliberately portrait — narrow and tall —
           to read as a vertical rectangle rather than a square. Sized as a
           percentage of the media column so it stays proportional at every
           breakpoint. */
        .afy-media-accent {
          position: absolute;
          left: -6%;
          bottom: -10%;
          width: 32%;
          height: 92%;
          background: var(--vibe-color-primary);
          z-index: 0;
          pointer-events: none;
        }
        .afy-media-frame {
          position: relative;
          z-index: 1;
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }
        .afy-media-slot {
          position: absolute;
          inset: 0;
        }
        .afy-media-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .afy-media-img--empty {
          background: var(--vibe-color-surface);
        }
        /* All matched amenity photos stay mounted and stacked; only opacity
           changes on select. The inactive layers rest beneath (z-index 1,
           opacity 0); the active layer rises on top (z-index 2) and fades in
           while the previously-active layer stays opaque underneath until the
           crossfade completes — no base-image bleed-through, no undecoded pop. */
        .afy-media-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 1;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .afy-media-overlay--active {
          z-index: 2;
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .afy-media-overlay { transition: none; }
        }

        /* ── Right column: heading + copy + CTA + tiles ── */
        .afy-heading {
          font-family: var(--vibe-font-heading);
          /* Standardized Home section-title size — matches PropertyOverview,
             FloorplansList, NeighborhoodSection, ResidentReview, SocialFeedSection. */
          font-size: clamp(2.25rem, 4vw, 3rem);
          font-weight: 700;
          line-height: 1.05;
          margin: 0 0 1.25rem;
          color: var(--vibe-color-primary);
        }
        .afy-body {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
          margin: 0 0 1.25rem;
          max-width: 34rem;
        }
        .afy-body [data-vibesite-richtext] p {
          margin: 0 0 1em;
        }
        .afy-body [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }
        .afy-body [data-vibesite-richtext] a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* Positions the shared .site-cta pill in the copy column. */
        .afy-cta-wrap {
          display: inline-block;
          margin-bottom: 1.75rem;
        }
        /* Neutralize the richtext wrapper inside the pill so nothing overrides
           the button typography. */
        .afy-cta [data-vibesite-richtext],
        .afy-cta [data-vibesite-richtext] p,
        .afy-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        /* ── Amenity tiles: icon over label, 3 columns x 2 rows ── */
        .afy-tiles {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          grid-auto-rows: auto;
          gap: 1rem 1rem;
          max-width: 36rem;
        }
        .afy-tile-slot {
          display: block;
        }
        /* Individual tile — icon on top, label below. Neutral text/icon at
           rest, primary color when hovered/active/keyboard-focused (matches
           the highlighted "24/7 Fitness Center" in the mock). */
        .afy-tile {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.375rem 0.25rem;
          background: transparent;
          border: none;
          color: var(--vibe-color-text);
          font-family: var(--vibe-font-heading);
          font-size: 0.9375rem;
          font-weight: 700;
          line-height: 1.25;
          text-align: center;
          cursor: pointer;
          transition: color 0.25s ease, transform 0.25s ease;
        }
        .afy-tile-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 2.5rem;
          color: inherit;
        }
        .afy-tile-icon svg {
          width: 100%;
          height: 100%;
          stroke-width: 1.4;
        }
        .afy-tile-label {
          display: block;
          min-width: 0;
          overflow-wrap: break-word;
          word-break: normal;
          hyphens: manual;
          text-wrap: balance;
        }
        .afy-tile:hover,
        .afy-tile:focus-visible {
          color: var(--vibe-color-primary);
          transform: translateY(-2px);
        }
        .afy-tile:focus-visible {
          outline: none;
        }
        .afy-tile:focus-visible .afy-tile-label {
          text-decoration: underline;
          text-decoration-thickness: 2px;
          text-underline-offset: 4px;
        }
        .afy-tile--active {
          color: var(--vibe-color-primary);
        }

        /* ── Mobile / tablet: image → heading → body → CTA → tiles, tightened.
           In the single-column layout the photo is inset from its column so
           the primary-color accent can bleed out past the container padding to
           the viewport edge and drop below the photo, matching the offset
           composition of the design. The negative left offset and matching
           extra width cancel out the parent container's horizontal padding
           (sm:px-6 = 1.5rem, px-4 = 1rem on phones). The copy column is also
           centered so the heading, body, and CTA line up on narrow screens;
           desktop reclaims the left-aligned layout via the grid rules above. ── */
        @media (max-width: 1023px) {
          .afy-media-frame {
            width: 85%;
            margin-left: auto;
            margin-right: auto;
          }
          .afy-media-accent {
            left: -1.5rem;
            bottom: -8%;
            width: calc(32% + 1.5rem);
            height: 95%;
          }
          .afy-content {
            align-items: center;
            text-align: center;
          }
          .afy-heading {
            text-align: center;
          }
          .afy-body {
            margin-left: auto;
            margin-right: auto;
          }
        }
        /* Tablet: image stacks above copy and the accent rectangle bleeds
           further below the photo — pad the content column down so the
           heading clears that overlap comfortably. */
        @media (min-width: 641px) and (max-width: 1023px) {
          .afy-content {
            padding-top: 3rem;
          }
        }
        @media (max-width: 640px) {
          .afy-media-frame {
            width: 82%;
            margin-left: auto;
            margin-right: auto;
          }
          .afy-media-accent {
            left: -1rem;
            bottom: -6%;
            width: calc(32% + 1rem);
            height: 95%;
          }
          .afy-tiles {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 0.875rem 0.5rem;
          }
          .afy-tile {
            font-size: 0.8125rem;
          }
          .afy-tile-icon {
            width: 2.25rem;
            height: 2.25rem;
          }
        }
        @media (max-width: 420px) {
          .afy-tiles {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesForYou',
  displayName: 'Amenities For You',
  tokens: {
    'amenitiesforyou.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Amenities for you</p>',
    },
    'amenitiesforyou.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue:
        "<p>Student living is effortless with amenities that support both focus and fun. Stay on top of your studies with high-speed Wi-Fi, private study lounges, and a fully equipped business center. When it's time to recharge, relax at the resort-style pool, break a sweat in the 24-hour fitness center, or connect with friends in our lively social spaces.</p>",
    },
    'amenitiesforyou.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>View All Amenities</p>',
    },
  },
  slotGroups: {
    'amenitiesforyou.image': imageDef,
    'amenitiesforyou.cta_link': ctaDef,
    'amenitiesforyou.amenity_1': amenity1Def,
    'amenitiesforyou.amenity_2': amenity2Def,
    'amenitiesforyou.amenity_3': amenity3Def,
    'amenitiesforyou.amenity_4': amenity4Def,
    'amenitiesforyou.amenity_5': amenity5Def,
    'amenitiesforyou.amenity_6': amenity6Def,
  },
};

(AmenitiesForYou as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesForYou;
